import * as z from 'zod'
import { dateSchema } from '~/shared/lib/schemas'
import { ApiModel } from './core'
import { PenaltyRecipientByOdps, PenaltySource } from './penaltyCreate'

const attributesSchema = z.object({
  source: z.string(),
  protocolDate: z.string(),
  protocolNumber: z.string(),
  odpsCode: z.string(),
  odpsName: z.string(),
  odpsAddr: z.string(),
  penaltyDatetime: z.string(),
  penaltyLocation: z.string(),
  penaltyLawArticleCode: z.string(),
  penaltyLawArticleNumber: z.string(),
  penaltyLawArticleDescription: z.string(),
  penaltyPhotoUrl: z.string(),
  penaltyMetaData: z.string(),
  carExtId: z.string(),
  carPlateNumber: z.string(),
  carVin: z.string(),
  carBrandTitle: z.string(),
  carModelTitle: z.string(),
  carSts: z.string(),
  carMetaData: z.string(),
  paymentOrgInn: z.string(),
  paymentOrgName: z.string(),
  paymentUin: z.string(),
  paymentType: z.string(),
  paymentPayIdentifier: z.string(),
  paymentAmount: z.number(),
  paymentDiscountSize: z.string(),
  paymentDiscountDate: z.string(),
  dayUntilDiscountEnd: z.number(),
  dayUntilPayEnd: z.number(),
  paymentAmountTotal: z.number(),
  paymentUntilDate: z.string(),
  paymentIsPaid: z.boolean(),
  paymentAdditionalInfo: z.string(),
  paymentMetaData: z.string(),
  recipient: z.string(),
  recipientInn: z.string(),
  recipientKpp: z.string(),
  recipientBankAccountNumber: z.string(),
  recipientBankCorrAccountNumber: z.string(),
  recipientBankName: z.string(),
  recipientBankBik: z.string(),
  recipientBankPurpose: z.string(),
  recipientBankUsername: z.string(),
  recipientBankKbk: z.string(),
  recipientBankOktmo: z.string(),
  recipientBankSign: z.string(),
  recipientBankBilldate: z.string(),
  tmsSubdivision: z.string(),
  tmsSubdivisionName: z.string(),
  driverExtId: z.string(),
  tmsDriver: z.string(),
  tmsDriverName: z.string(),
  comment: z.string(),
  bankclientExported: z.boolean(),
  bankclientLastExportAmount: z.number(),
  bankclientLastExportDate: z.string(),
  files: z.array(
    z.object({
      uuid: z.string(),
      previewUrl: z.string(),
      originalUrl: z.string(),
    }),
  ),
})

const schema = z
  .object({
    createdAt: dateSchema,
    updatedAt: dateSchema,
  })
  .merge(attributesSchema)

export type PenaltyAttributes = z.infer<typeof schema>

export type PenaltyExportBankclient = {
  startNumber: number | string
  companyBankDetailId: UniqueId
  penaltiesIds: string[]
}

export class Penalty extends ApiModel<typeof schema, PenaltyAttributes> {
  static jsonApiType = 'penalties'

  static schema = schema

  getFiles(): PenaltyAttributes['files'] {
    return this.getAttribute('files')
  }

  getPenaltyDatetime(): string {
    return this.getAttribute('penaltyDatetime')
  }

  getCreatedAt(): string {
    return this.getAttribute('createdAt')
  }

  getProtocolNumber(): string {
    return this.getAttribute('protocolNumber')
  }

  getProtocolDate(): string {
    return this.getAttribute('protocolDate')
  }

  getSource(): string {
    return this.getAttribute('source')
  }

  isManual(): boolean {
    return this.getSource() === PenaltySource.manual
  }

  /*
   * Реквизиты получателя из последнего загруженного штрафа того же подразделения ГАИ.
   * Используются для подстановки в форму ручного создания.
   * */
  static async getRecipientByOdps(
    odpsCode: string,
  ): Promise<PenaltyRecipientByOdps> {
    const url = `${Penalty.getJsonApiUrl()}/actions/recipient-by-odps`
    const client = Penalty.httpClient.getImplementingClient()

    const response = await client.get<PenaltyRecipientByOdps>(url, {
      params: { odpsCode },
    })

    return response.data
  }

  static async exportBankclientTXT({
    startNumber,
    companyBankDetailId,
    penaltiesIds,
  }: PenaltyExportBankclient) {
    const url = `${this.getJsonApiUrl()}/actions/export-bankclient-txt`
    const client = Penalty.httpClient.getImplementingClient()
    const res = await client.post(
      url,
      {
        startNumber,
        companyBankDetailId,
        additionalFilterById: penaltiesIds,
      },
      {
        responseType: 'blob',
      },
    )
    return res.data
  }

  static async exportBankclientEXCEL({
    startNumber,
    companyBankDetailId,
    penaltiesIds,
  }: PenaltyExportBankclient) {
    const url = `${this.getJsonApiUrl()}/actions/export-bankclient-excel`
    const client = Penalty.httpClient.getImplementingClient()
    const res = await client.post(
      url,
      {
        startNumber,
        companyBankDetailId,
        additionalFilterById: penaltiesIds,
      },
      {
        responseType: 'blob',
      },
    )
    return res.data
  }

  getBrandModel(): string {
    const brand = this.getAttribute('carBrandTitle')
    const model = this.getAttribute('carModelTitle')

    if (brand && model) return `${brand} ${model}`
    if (brand) return brand
    if (model) return model

    return ''
  }

  static async getIdList(filters: Record<string, string>): Promise<string[]> {
    const url = `${Penalty.getJsonApiUrl()}/actions/get-id-list`
    const client = Penalty.httpClient.getImplementingClient()

    const res = await client.get<{ idList: number[] }>(url, {
      params: filters,
    })
    return res.data?.idList.map(String)
  }

  static async getAmountSum(penaltiesIds: string[]): Promise<number> {
    const url = `${Penalty.getJsonApiUrl()}/actions/get-amount-sum`
    const client = Penalty.httpClient.getImplementingClient()

    const res = await client.post<{ paymentAmountSum: number | undefined }>(
      url,
      {
        additionalFilterById: penaltiesIds,
      },
    )
    return res.data?.paymentAmountSum || 0
  }

  static async exportPenalty(filters: string): Promise<Blob> {
    const url = `${Penalty.getJsonApiUrl()}/actions/export-excel?${filters}`
    const client = Penalty.httpClient.getImplementingClient()

    const response = await client.post(url, undefined, {
      responseType: 'blob',
    })

    return response?.data
  }
}
